import { useState } from 'react';
import useGetDeliveryLocation from '../../../modules/mobile/common/hooks/useGetDeliveryLocation';
import { CompanyDetailsInterface, DeliveryQuote, DeliveryZone } from '../../../services/exports/Interfaces';
import DeliveryQuotesManager from '../../../services/api/DeliveryQuotesManager';
import { DeliveryAddressSchema } from '../../../modules/mobile/modals/delivery-address/form/schema';
import DeliveryZonesManager from '../../../services/api/DeliveryZonesManager';
import { ERROR_CODES } from '../../../services/exports/Constants';

interface Props {
  company: CompanyDetailsInterface;
  scheduledFor?: string;
}

export enum ValidationErrors {
  address_not_found = 'address_not_found',
  address_not_serviceable = 'address_not_serviceable',
  no_drivers_available = 'no_drivers_available',
}

interface ValidatedDeliveryData {
  delivery_quote?: DeliveryQuote;
  delivery_zone: DeliveryZone;
}

const useDeliveryMethodValidation = (props: Props) => {
  const { company, scheduledFor } = props;
  const { getLocation, loading: locationLoading } = useGetDeliveryLocation(company);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const resetError = () => setValidationError(null);

  async function getDeliveryQuote(
    location: google.maps.LatLng,
    data: DeliveryAddressSchema,
  ): Promise<ValidatedDeliveryData> {
    const { success, response } = await DeliveryQuotesManager.create(
      company?.id,
      {
        dropoff_time: scheduledFor,
        street_name: data.delivery_street_name,
        street_number: data.delivery_street_number,
        city: data.delivery_city,
        state: company?.state,
        country: company?.country,
        zip_code: data.delivery_zip_code,
        lat: location.lat(),
        lng: location.lng(),
      },
      {
        [ERROR_CODES.NO_DELIVERY_QUOTES_AVAILABLE]: () => setValidationError(ValidationErrors.no_drivers_available),
        [ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE]: () => setValidationError(ValidationErrors.address_not_serviceable),
      },
    );

    if (!success) {
      return;
    }

    const deliveryZone = await getDeliveryZone(location, data);

    if (!deliveryZone) {
      return;
    }

    return {
      delivery_zone: deliveryZone,
      delivery_quote: response.data.data,
    };
  }

  async function getDeliveryZone(location: google.maps.LatLng, data: DeliveryAddressSchema): Promise<DeliveryZone> {
    const { success, response } = await DeliveryZonesManager.search(
      {
        company_id: company?.id,
        lat: location.lat(),
        lng: location.lng(),
        zip: data.delivery_zip_code,
      },
      {
        [ERROR_CODES.ADDRESS_NOT_FOUND]: () => setValidationError(ValidationErrors.address_not_found),
        [ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE]: () => setValidationError(ValidationErrors.address_not_serviceable),
      },
    );

    if (!success) {
      return;
    }

    return response.data.data;
  }

  async function validate(data: DeliveryAddressSchema): Promise<ValidatedDeliveryData> {
    resetError();
    setLoading(true);

    const { success, location } = await getLocation(data);

    if (!success) {
      setLoading(false);
      setValidationError(ValidationErrors.address_not_found);

      return;
    }

    const deliveryMethodPromise =
      company?.delivery_mode === 'internal'
        ? getDeliveryZone(location, data).then((res) => (res ? { delivery_zone: res } : null))
        : getDeliveryQuote(location, data);

    const result = await deliveryMethodPromise;

    setLoading(false);

    return result;
  }

  return {
    validate,
    loading: locationLoading || loading,
    validationError,
    resetError,
  };
};

export default useDeliveryMethodValidation;
